<template>
    <div class="clients__page clients__page--view settings__page" :class="{ 'disabled': paymentVerificationLoading || pageLoading }">
        <div>
            <FormLabel title="Billings & Plans" html-for="info" label-type="tertiary" class="text--medium" />
            
            <div>
                <template v-if="accountPlansLoading">
                    <PageSpinner />
                </template>
                
                <template v-else>
                    <div v-if="['switch_pending', 'switch_free_pending', 'pending'].includes(subscriptionInfo?.status)" class="mb--25 mt--15">
                        <Alert type="primary" size="sm" :show-icon="false">
                            <template #text>
                                <div v-if="subscriptionInfo?.status === 'switch_pending'">
                                    <p class="text--sm text--semi--bold mb--5">
                                        Upgrading account plan in progress...
                                    </p>
                                    <p class="text--sm">
                                        Please wait while we process your account upgrade. This will only take a moment. If no change happens in the next hour, please contact <router-link to="/support" target="_blank" class="button--link text--semi--bold text--underline">
                                            support
                                        </router-link>
                                    </p>
                                </div>

                                <div v-else-if="subscriptionInfo?.status === 'switch_free_pending'">
                                    <p class="text--sm text--semi--bold mb--5">
                                        Switching account plan in progress...
                                    </p>
                                    <p class="text--sm">
                                        Please wait while we process your account downgrade. This will only take a moment. If no change happens in the next hour, please contact <router-link to="/support" target="_blank" class="button--link text--semi--bold text--underline">
                                            support
                                        </router-link>
                                    </p>
                                </div>

                                <div v-else-if="subscriptionInfo?.status === 'pending'">
                                    <p class="text--sm text--semi--bold mb--5">
                                        Failed to upgrade account plan
                                    </p>
                                    <p class="text--sm">
                                        Please contact <router-link to="/support" target="_blank" class="button--link text--semi--bold text--underline">
                                            support
                                        </router-link>
                                        for more information.
                                    </p>
                                </div>
                            </template>
                        </Alert>  
                    </div>

                    <div v-else-if="subscriptionInfo?.nextPlanData" class="mb--25 mt--15">
                        <p v-if="subscriptionInfo?.nextPlanData.plan" class="text--sm">
                            You are currently enjoying the full benefits of our <strong class="text--capitalize">{{ accountPlan }}</strong> plan made available to you.
                            Upgrade to our <strong class="text--capitalize">{{ subscriptionInfo?.nextPlanData.plan }}</strong> plan at <strong>{{ $country.getSymbol(subscriptionInfo?.currency, 'currency') }}{{ $format_amount(subscriptionInfo?.nextPlanData?.monthlyAmount, 2) }}/month</strong> to access more features and integrations.
                        </p>
                    </div>

                    <div v-else-if="isCountryRestricted" class="mb--25 mt--15">
                        <Alert type="info" size="sm">
                            <template #text>
                                <p class="text--sm">
                                    Upgrading to a paid plan is currently unavailable in your location. Enjoy the benefits of using a free plan while we actively work to make paid plans available to you soon.
                                </p>
                            </template>
                        </Alert>
                    </div>

                    <div class="mt--15">
                        <BaseCard 
                            v-if="!['switch_pending', 'switch_free_pending', 'pending'].includes(subscriptionInfo?.status)"
                            custom-header-title
                            header-class="bg--blue--300 border--0 text--capitalize text--medium text--sm"
                            card-body-classes="bg--grey--200"
                        >
                            <template #card-header>
                                <p class="text--sm text-gray-600">
                                    Your current plan: <strong class="text--capitalize">{{ accountPlan }}</strong>
                                </p>
                            </template>
            
                            <template #card-body>
                                <div>
                                    <div class="flex align-items-center">
                                        <span class="text-gray-800 text--xl text--semi--bold mb--0">
                                            {{ $country.getSymbol(subscriptionInfo?.currency, 'currency') }}{{ $format_amount(subscriptionInfo?.amount, 2) }}
                                        </span>
                                        <span class="text--xs text-gray-500 mb--0">/month</span>
                                    </div>
            
                                    <div v-if="subscriptionInfo?.nextBillingDate" class="flex align-items-center mt--5">
                                        <span class="text--sm text-gray-600 mr--3">Your next billing date is <strong>{{ $format_date(subscriptionInfo?.nextBillingDate) }}</strong></span>
                                    </div>
                                </div>
            
                                <div class="mt--20 flex align-items-center">
                                    <BaseButton 
                                        v-if="!isCountryRestricted || accountPlan !== 'plus'"
                                        label="Upgrade plan"
                                        @submit="modalActionType = 'showBillingModal'"
                                    />
        
                                    <BaseButton 
                                        v-if="accountPlan !== 'free'"
                                        label="Cancel plan"
                                        variant="outline"
                                        class="ml--5"
                                        @submit="handleCancelSubscription"
                                    />
                                </div>
                            </template>
                        </BaseCard>
                    </div>
                </template>
            </div>
            
            <div>
                <div class="settings__auth__header mt--30 flex align-items-center justify-content-between">
                    <FormLabel title="Billing cards" html-for="info" label-type="title" class="text--semi--bold mb--0" />
                    <BaseButton 
                        v-if="!isCountryRestricted"
                        label="+ Add card"
                        bold
                        variant="ghost"
                        class="p--0"
                        :loading="addCardLoading"
                        @submit="addCard"
                    />
                </div>

                <SavedCards @toggleDeleteInfoModal="toggleDeleteInfoModal" />
            </div>

            <div class="w-100 mt--40">
                <BaseCard 
                    custom-header-title 
                    card-classes="border--0"
                    header-class="text-gray-700 text--medium bg--blue--300"
                >
                    <template #card-header>
                        <FormLabel title="Payment History" html-for="info" label-type="title" class="text--semi--bold mb--0" />
                    </template>

                    <template #card-body>
                        <PaymentHistory 
                            @open-invoice-modal="onOpenInvoiceModal"
                        />
                    </template>
                </BaseCard>
            </div>
        </div>

        <UpgradeBillingPlanModal 
            :show="modalActionType === 'showBillingModal'" 
            @cancel="modalActionType = ''" 
            @refresh-payment-list="updatePaymentList"
        />

        <ConfirmDeletion 
            type="card" 
            custom-msg 
            :show-modal="modalActionType === 'showRemovalInfoModal'" 
            :custom-footer="true"
            @cancel="toggleDeleteInfoModal({ bool: false })" 
        >
            <template #custom-msg>
                <p class="text--sm text-gray-600">
                    You’re currently on a paid plan. To delete this card, add a new replacement card or switch to a free plan.
                </p>
                <p class="text-gray-500 text--xs mt--5">
                    (This helps us retain an active card for your next billing cycle)
                </p>
            </template> 
            <template #custom-footer>
                <BaseButton 
                    label="Cancel" 
                    variant="outline" 
                    variant-style="secondary"
                    :loading="downgradeBtnLoading" 
                    @submit="modalActionType = ''"
                />
            </template>
        </ConfirmDeletion>

        <MainModal
            :show-modal="modalActionType === 'showReceiptModal'" 
            :footer="false"
            @close="modalActionType = ''"
        >
            <template #body>
                <Receipt :invoice-data="invoiceData" />
            </template>
        </MainModal>

        <ProgressSuccess 
            :show-modal="modalActionType === 'showPlanSuccessModal'"
            custom-text
            custom-img
            @close="modalActionType = ''"
        >   
            <template #customText>
                <p class="text-center text--color-info mt--15">
                    Your account plan has been successfully moved to <strong>{{ accountPlan }}</strong>
                </p>
            </template>
        </ProgressSuccess>
    </div>
</template>

<script>
import Paystack from "@paystack/inline-js";
import {
    BaseCard,
    BaseButton,
    FormLabel,
    PageSpinner,
    MainModal,
    ConfirmDeletion,
    Alert,
    ProgressSuccess,
} from "@/components/ui/index";
import Receipt from "@/components/billings/Receipt.vue";
import UpgradeBillingPlanModal from "@/components/ui/modals/UpgradeBillingPlan.vue";
import SavedCards from "@/components/payments/SavedCards.vue";
import PaymentHistory from "@/components/payments/PaymentHistory.vue";
import envs from "@/config/env";

export default {
    name: "BillingsAndPlans",

    components: {
        BaseButton,
        BaseCard,
        PaymentHistory,
        FormLabel,
        PageSpinner,
        MainModal,
        Receipt,
        SavedCards,
        UpgradeBillingPlanModal,
        ConfirmDeletion,
        Alert,
        ProgressSuccess,
    },

    beforeRouteEnter(to, from, next) {        
        next(async(vm) => {            
            if (to.name === "payment-settings") { 
                if (!vm.systemPlans.length || !vm.subscriptionInfo?.currency) {
                    await vm.fetchPageData(vm);
                }
            }
        });
    },

    data() {
        return {
            accountPlansLoading: false,
            accountPlansErr: false,
            addCardLoading: false,
            paymentVerificationLoading: false,
            downgradeBtnLoading: false,
            lastCardId: "",
            invoiceData: "",
            modalActionType: "",
            refreshPaymentList: false,
            pageLoading: false,
        };
    },

    computed: {
        subscriptionInfo() {
            return this.$store.getters["subscription/subscriptionInfo"];
        },

        accountPlan() {
            return this.subscriptionInfo?.plan || "free";
        },

        systemPlans() {
            return this.$store.getters["config/systemPlans"];
        },

        pageData() {
            return this.$store.getters["subscription/pageData"];
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },

        savedCards() {
            return this.$store.getters["subscription/savedCards"];
        },

        isCountryRestricted() {
            return this.$store.getters["app/isCountryRestricted"];
        },
    },

    mounted() {                           
        // Listen for subscription updates (auto-cleanup on unmount)
        this.$socketOn("subscriptionUpdated", (data) => {
            console.log("Updated subscription:", data);
            this.$store.dispatch("subscription/setSubscriptionInfo", data.subscription);
            this.$store.dispatch("subscription/setPaymentRecords", data.invoice);
        });

        // this.$refs.toast.success("Operation completed!");
    },

    methods: {
        toggleDeleteInfoModal({ bool, cardId }) {
            if (!bool) {
                this.modalActionType = "";
            } else {
                this.modalActionType = "showRemovalInfoModal";
            }
            this.lastCardId = cardId;
        },

        async paymentSuccess(response) {
            try {
                this.paymentVerificationLoading = true;

                const encryptedType = sessionStorage.getItem("z:t_type");
                const plan = sessionStorage.getItem("z:t_p");
                
                await this.$store.dispatch("subscription/verifyCharge", {
                    encryptedType,
                    reference: response.reference,
                    targetPlan: plan,
                });

                this.$toast.success("New card added");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;
                this.$toast.error(errMsg);
            } finally {
                this.paymentVerificationLoading = false;
            }
        },

        paymentClosed() {
            this.$toast.error("Process cancelled");
        },
        
        async addCard() {
            if (this.subscriptionInfo?.status === "switch_pending" || this.subscriptionInfo?.status === "pending") {
                this.$toast.error("Adding a new card not allowed for a pending account plan");
                return;
            }

            if (this.accountPlan === "free") {
                this.$toast.info("Switch to a paid plan to add a card");
                return;
            }

            if (this.savedCards.length >= 1) {
                this.$toast.info("Only one card allowed per user");
                return;
            }

            try {
                this.addCardLoading = true;

                const popup = new Paystack();
                
                popup.newTransaction({
                    key: envs.VUE_APP_PAYSTACK_TEST_URL,
                    email: this.profile.email,
                    ref: `ZTNX-${Math.random().toString(36).substring(7)}`,
                    channels: ["card"],
                    metadata: {
                        custom_fields: [
                            {
                                display_name: "Mobile Number",
                                variable_name: "mobile_number",
                                value: this.profile.phoneNumber || "", // Optional: User's mobile number
                            },
                        ],
                        purpose: "add_new_card",
                    },
                    amount: 10000,
                    onLoad: async(response) => { // update the user's payment provider info 
                        console.log("onLoad: ", response);
                        sessionStorage.setItem("z:t_p", this.subscriptionInfo?.plan);
                        sessionStorage.setItem("z:t_type", "wkjfwdnvs");

                        if (!this.subscriptionInfo?.paymentProviderData?.customerId) {
                            await this.$store.dispatch("subscription/updateSubscriptionInfo", {
                                ...this.subscriptionInfo,
                                paymentProviderData: {
                                    ...this.subscriptionInfo?.paymentProviderData,
                                    name: "Paystack",
                                    customerId: response.customer.id,
                                },
                            });
                        }
                    },
                    onSuccess: this.paymentSuccess, // add card, then subscribe user to recurring plan
                    onCancel: this.paymentClosed,
                });

                // savedCardsCount
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;                
                this.$toast.error(errMsg);
            } finally {
                this.addCardLoading = false;
            }
        },

        async fetchPageData(vm) {            
            try {
                vm.accountPlansLoading = true;

                await vm.$store.dispatch("config/fetchSystemPlans");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                vm.$toast.error(errMsg);
                vm.accountPlansErr = true;
            } finally {
                vm.accountPlansLoading = false;
            }
        },
        
        onOpenInvoiceModal(data) {            
            this.invoiceData = data;
            this.modalActionType = "showReceiptModal";
        },

        handleUpgradePlanSuccess() {
            this.modalActionType = "showPlanSuccessModal";
        },

        updatePaymentList(bool) {
            this.refreshPaymentList = bool;
        },

        async handleCancelSubscription() {
            try {
                this.pageLoading = true;

                await this.$store.dispatch("subscription/cancelPaidPlan");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error.message;                
                this.$toast.error(errMsg);
            } finally {
                this.pageLoading = false;
            }
        },
    },
};
</script>